import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { ICompanyInfo } from 'commons/types';
import {
  getCompanyInfo,
  updateCompanyInfo,
} from 'redux-stores/actions/companyInfo.action';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { ListPage } from '../commons/components';

import S from './CompanyInfo.styles';
import CompanyAPJInfoComponent from './components/CompanyAPJInfo.component';

const CompanyInfoPage: React.FC = () => {
  const { company } = useSelector(
    (state: RootReducerInterface) => state.company,
  );

  const [companyInfoState, setCompanyInfoState] = useState<ICompanyInfo | null>(
    company,
  );

  const dispatch = useDispatch<AppDispatch>();

  const updateCompanyInfoSearchDebounce = useDebouncedCallback(
    (_searchParams, _location, _dispatch) => {
      _dispatch(
        getCompanyInfo({
          locationId: _location,
        }),
      );
    },
    200,
    { maxWait: 1000 },
  );

  useEffect(() => {
    setCompanyInfoState(company);
  }, [company, dispatch]);

  const onFieldChange = (key: keyof ICompanyInfo, value: string) => {
    if (companyInfoState) {
      setCompanyInfoState({
        ...companyInfoState,
        [key]: value,
      });
    }
  };

  const handleSave = () => {
    if (companyInfoState) {
      dispatch(updateCompanyInfo(companyInfoState));
    }
  };

  return (
    <ListPage updateListSearchDebounce={updateCompanyInfoSearchDebounce}>
      <Card style={{ overflow: 'visible' }}>
        <CardContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <S.InfoWrapper container style={{ flexDirection: 'column' }}>
            <S.Title>Company Info</S.Title>
            <S.Subtitle>Basic Information</S.Subtitle>
            <Grid item xs={12}>
              <S.Label>Name</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.name || ''}
                id={`field-company-info-name-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) => onFieldChange('name', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Abbreviation</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.abbreviation || ''}
                id={`field-company-info-abbreviation-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('abbreviation', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Address</S.Label>
              <S.TextArea
                minRows={4}
                maxRows={6}
                placeholder="-"
                value={companyInfoState?.address || ''}
                id={`field-company-info-address-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('address', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>City</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.city || ''}
                id={`field-company-info-city-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) => onFieldChange('city', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Phone</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.phone || ''}
                id={`field-company-info-phone-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) => onFieldChange('phone', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>NPWP</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.npwp || ''}
                id={`field-company-info-npwp-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) => onFieldChange('npwp', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>NPWP Address</S.Label>
              <S.TextArea
                minRows={4}
                maxRows={6}
                placeholder="-"
                value={companyInfoState?.npwp_address || ''}
                id={`field-company-info-npwp-address-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('npwp_address', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Pbf Number</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.pbf_no || ''}
                id={`field-company-info-pbf-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('pbf_no', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Dak Number</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.dak_no || ''}
                id={`field-company-info-dak-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('dak_no', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Cdob Number</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.cdob_no || ''}
                id={`field-company-info-cdob-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('cdob_no', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Cdakb Number</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.cdakb_no || ''}
                id={`field-company-info-cdakb-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('cdakb_no', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>Cdob CCP Number</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.cdob_ccp_no || ''}
                id={`field-company-info-cdob-ccp-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('cdob_ccp_no', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>VA BCA</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.va_bca || ''}
                id={`field-company-info-bca-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('va_bca', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <S.Label>VA Mandiri</S.Label>
              <TextField
                variant="outlined"
                placeholder="-"
                value={companyInfoState?.va_mandiri || ''}
                id={`field-company-info-mandiri-${companyInfoState?.name}`}
                style={{ width: '100%' }}
                onChange={(event) =>
                  onFieldChange('va_mandiri', event.target.value)
                }
              />
            </Grid>
            {/* APJ Info Section */}
            <CompanyAPJInfoComponent
              companyInfoState={companyInfoState}
              setCompanyInfoState={setCompanyInfoState}
            />
          </S.InfoWrapper>
          <Button
            variant="contained"
            onClick={handleSave}
            style={{
              width: '400px',
              marginTop: '24px',
            }}
          >
            <Typography variant="button">Save</Typography>
          </Button>
        </CardContent>
      </Card>
    </ListPage>
  );
};

export default CompanyInfoPage;
